import { Shield, ArrowRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';
import { useState } from 'react';

interface SliderQuestion {
  id: string;
  label: string;
  leftLabel: string;
  rightLabel: string;
}

const questions: SliderQuestion[] = [
  {
    id: 'leadership',
    label: 'Security Leadership',
    leftLabel: 'No one accountable',
    rightLabel: 'Board-level CISO',
  },
  {
    id: 'strategy',
    label: 'Security Strategy',
    leftLabel: 'Nothing documented',
    rightLabel: 'Comprehensive & approved',
  },
  {
    id: 'incidents',
    label: 'Incident Readiness',
    leftLabel: 'No plan in place',
    rightLabel: 'Tested & exercised',
  },
  {
    id: 'culture',
    label: 'Security Culture',
    leftLabel: "It's IT's problem",
    rightLabel: 'Everyone owns security',
  },
];

export default function SecurityReadinessAssessment() {
  const [showAssessment, setShowAssessment] = useState(false);
  const [scores, setScores] = useState<Record<string, number>>({
    leadership: 3,
    strategy: 3,
    incidents: 3,
    culture: 3,
  });

  const handleScoreChange = (id: string, value: number) => {
    setScores({ ...scores, [id]: value });
  };

  const averageScore = Object.values(scores).reduce((a, b) => a + b, 0) / questions.length;

  const getBucket = () => {
    if (averageScore <= 1.5) {
      return {
        title: 'Critical Risk',
        description:
          'Your organisation has fundamental security gaps across multiple areas. Without immediate leadership and structure, you are exposed to significant and likely breach.',
      };
    }
    if (averageScore <= 2.3) {
      return {
        title: 'Early Stages',
        description:
          'You have some awareness of security needs but little formal structure. Risks are mounting and leadership is needed to establish proper foundations.',
      };
    }
    if (averageScore <= 3.0) {
      return {
        title: 'Building Foundations',
        description:
          'Basic security measures exist in pockets but lack coordination and strategy. You are doing some things right but without direction, effort is often wasted.',
      };
    }
    if (averageScore <= 3.7) {
      return {
        title: 'Developing',
        description:
          'Your security programme has decent coverage but gaps remain. Strategic oversight would help prioritise investments and ensure nothing critical is missed.',
      };
    }
    if (averageScore <= 4.3) {
      return {
        title: 'Established',
        description:
          'You have a functioning security operation with most key elements in place. Focus should be on optimisation, efficiency, and preparing for emerging threats.',
      };
    }
    return {
      title: 'Advanced',
      description:
        'Your security posture is mature and well-governed. The focus now is continuous improvement, scaling, succession planning, and ensuring security enables rather than constrains the business.',
    };
  };

  const bucket = getBucket();

  return (
    <section className="py-20 bg-primary relative overflow-hidden">
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
        }}
      />

      <div className="container-custom relative">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 border border-teal/30 mb-6">
              <Shield className="h-4 w-4 text-teal" />
              <span className="text-sm font-medium text-teal">Security Snapshot</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-4">
              How Mature Is Your Security Posture?
            </h2>
            <p className="text-primary-foreground/70 max-w-xl mx-auto">
              {showAssessment
                ? 'Adjust the sliders to reflect your current state.'
                : 'Click below for a super quick review of your security posture.'}
            </p>
          </div>

          {!showAssessment ? (
            /* Initial State - Show Button without container */
            <div className="text-center">
              <button onClick={() => setShowAssessment(true)} className="btn-hero px-5 py-3">
                <Play className="mr-3 h-5 w-5" />
                Take the Test
              </button>
            </div>
          ) : (
            /* Assessment State - Show Sliders with container */
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
              {/* Sliders - 5 points each */}
              <div className="space-y-6 mb-8">
                {questions.map(q => (
                  <div key={q.id} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-primary-foreground">{q.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-primary-foreground/50 w-24 text-right hidden sm:block">
                        {q.leftLabel}
                      </span>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        step="1"
                        value={scores[q.id]}
                        onChange={e => handleScoreChange(q.id, parseInt(e.target.value))}
                        className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-teal [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((scores[q.id] - 1) / 4) * 100}%, rgba(255,255,255,0.2) ${((scores[q.id] - 1) / 4) * 100}%, rgba(255,255,255,0.2) 100%)`,
                        }}
                      />
                      <span className="text-xs text-primary-foreground/50 w-24 hidden sm:block">
                        {q.rightLabel}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-primary-foreground/40 sm:hidden">
                      <span>{q.leftLabel}</span>
                      <span>{q.rightLabel}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Live Result */}
              <div className="border-t border-white/10 pt-6">
                <div className="text-center mb-6">
                  <span className="text-sm text-primary-foreground/60 uppercase tracking-wide">
                    Your Security Posture
                  </span>
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-teal mt-2 mb-3">
                    {bucket.title}
                  </h3>
                  <p className="text-primary-foreground/70 text-sm max-w-lg mx-auto">
                    {bucket.description}
                  </p>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <p className="text-primary-foreground mb-4">
                    Would you like to{' '}
                    <Link to="/services/security-strategy" className="text-teal hover:underline">
                      strengthen your security posture
                    </Link>
                    ?
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-hero"
                  >
                    Book a Discovery Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
